import { readingMaterialsSchema } from "./reading-progression";
export * from "./recommendation-contracts";
export * from "./flashcards";
import { z } from "zod";
import { lauteExerciseConfigSchema, lauteWordsSchema } from "./laute-erkennen";
export * from "./laute-erkennen";
export * from "./transkodieren-contracts";
import { transkodierenExerciseConfigSchema } from "./transkodieren-contracts";
import { einmaleinsReiheSchema, einmaleinsExerciseConfigSchema, einmaleinsRunSettingsSchema } from "./einmaleins-contracts";
export { einmaleinsReiheSchema, einmaleinsExerciseConfigSchema, einmaleinsRunSettingsSchema } from "./einmaleins-contracts";
export { adaptiveEinmaleinsConfigSchema, type AdaptiveEinmaleinsConfig } from "./learning-contracts";
import { exerciseTypeSchema, type ExerciseType } from "./exercise-types";
import { matheOperationSchema, matheZahlenraumSchema } from "./mathe-contracts";

export { EXERCISE_TYPES, exerciseTypeSchema } from "./exercise-types";
export type { ExerciseType } from "./exercise-types";
export { matheOperationSchema, matheZahlenraumSchema } from "./mathe-contracts";
export type { MatheOperation, MatheZahlenraum } from "./mathe-contracts";

export {
  adaptiveAssignmentCreateInputSchema,
  adaptiveAssignmentUpdateInputSchema,
  adaptiveBlitzlesenConfigSchema,
  adaptiveMatheConfigSchema,
  exerciseAnswerRegistry,
  exerciseAnswerSchema,
  recommendationInputSchema,
  bookmarkInputSchema,
  runStartInputSchema,
} from "./learning-contracts";
export type { AdaptiveAssignmentCreateInput, AdaptiveAssignmentUpdateInput, AdaptiveBlitzlesenConfig, AdaptiveMatheConfig, ExerciseAnswerInput, RunStartInput } from "./learning-contracts";

export { GRUNDWORTSCHATZ_HESSEN } from "./blitzlesen-grundwortschatz";
export { GRUNDWORTSCHATZ_HESSEN_SYLLABLES } from "./blitzlesen-grundwortschatz-silben";
export { PSEUDOWOERTER, PSEUDOWOERTER_1, PSEUDOWOERTER_2 } from "./blitzlesen-pseudowoerter";
export { BLITZLESEN_EINFACHE_SILBEN, BLITZLESEN_KONSONANTENVERBINDUNGEN } from "./blitzlesen-silben";
export { NICHT_REIM_PAARE, REIM_PAARE } from "./reime-paare";
export {
  DEHNUNGS_H_CATEGORIES,
  DEHNUNGS_H_ITEMS,
  KURZE_LANGE_VOKALE_CATEGORIES,
  KURZE_LANGE_VOKALE_ITEMS,
} from "./rechtschreibung-woerter";
export { WORTARTEN_CATEGORIES, WORTARTEN_GENERATOR_SAETZE, WORTARTEN_UEBUNG_1 } from "./wortarten-saetze";
export { buildWordartenGeneratorPools, WORTARTEN_GENERATOR_POOL_COUNTS, type WordartenGeneratorFamily } from "./wortarten-generator";
export { GROSSKLEIN_RULES, GROSSKLEIN_SAETZE } from "./grossklein-saetze";
export { SATZGLIEDER_ROLES, SATZGLIEDER_SAETZE } from "./satzglieder-saetze";
export type { SatzgliedRole, SatzgliederSourceSentence, SatzgliederVariant } from "./satzglieder-saetze";
export { buildSatzgliederGeneratorSentence, satzgliederGeneratorFamilies, SATZGLIEDER_GENERATOR_POOL_COUNTS, SATZGLIEDER_GENERATOR_TOTALS } from "./satzglieder-generator";
export type { SatzgliederGeneratorFamily } from "./satzglieder-generator";
export { ZEITFORMEN, ZEITFORMEN_SAETZE, ZEITFORMEN_SCHWIERIGKEITEN } from "./zeitformen-saetze";
export type { Zeitform, ZeitformenSchwierigkeit } from "./zeitformen-saetze";
export { ZEITFORMEN_KONJUGIEREN_PERSONEN, ZEITFORMEN_KONJUGIEREN_VERBEN, formsForZeitformenKonjugieren } from "./zeitformen-konjugieren-verben";
export type { ZeitformenKonjugierenVerb } from "./zeitformen-konjugieren-verben";
export { SILBENTRENNUNG_V1_WORDS } from "./silbentrennung-woerter";
export type { SilbentrennungWordSource } from "./silbentrennung-woerter";
export { MORPHEM_STAMM_V1_ITEMS } from "./morphem-stamm-items";
export type { MorphemStammItemSource } from "./morphem-stamm-items";
export { MORPHEM_ZERLEGUNG_V1_ITEMS } from "./morphem-zerlegung-items";
export type { MorphemZerlegungItemSource } from "./morphem-zerlegung-items";

export const contentBlockSchema = z.discriminatedUnion("type", [
  z.object({
    id: z.string().min(1),
    type: z.literal("heading"),
    level: z.union([z.literal(2), z.literal(3)]),
    text: z.string().min(1).max(240),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("paragraph"),
    text: z.string().min(1).max(10_000),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("callout"),
    tone: z.enum(["info", "tip", "important", "rule", "example"]),
    title: z.string().min(1).max(160),
    text: z.string().min(1).max(5_000),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("image"),
    mediaId: z.string().min(1),
    alt: z.string().max(500),
    caption: z.string().max(500).optional(),
  }),
]);

export const contentDocumentSchema = z.array(contentBlockSchema).max(250);

export const legalPageSlugSchema = z.enum(["impressum", "datenschutz"]);
export const legalPageInputSchema = z.object({
  title: z.string().trim().min(1).max(160),
  blocks: contentDocumentSchema.min(1, "Mindestens ein Inhaltsblock ist erforderlich."),
});

export const quizOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(1_000),
});

const quizQuestionInputSchema = z
  .object({
    id: z.string().min(1),
    prompt: z.string().min(1).max(5_000),
    options: z.array(quizOptionSchema).min(2).max(8),
    correctOptionIds: z.array(z.string().min(1)).min(1).max(8).optional(),
    // Lesekompatibilität für bereits veröffentlichte Pilot-Konfigurationen.
    correctOptionId: z.string().min(1).optional(),
    explanation: z.string().max(5_000).optional(),
  })
  .superRefine((question, context) => {
    const correctOptionIds = question.correctOptionIds ?? (question.correctOptionId ? [question.correctOptionId] : []);
    if (correctOptionIds.length === 0) {
      context.addIssue({
        code: "custom",
        path: ["correctOptionIds"],
        message: "Mindestens eine Antwort muss als richtig markiert sein.",
      });
    }
    if (new Set(correctOptionIds).size !== correctOptionIds.length) {
      context.addIssue({ code: "custom", path: ["correctOptionIds"], message: "Richtige Antworten dürfen nicht doppelt vorkommen." });
    }
    for (const optionId of correctOptionIds) {
      if (!question.options.some((option) => option.id === optionId)) {
        context.addIssue({
          code: "custom",
          path: ["correctOptionIds"],
          message: "Jede richtige Antwort muss einer vorhandenen Option entsprechen.",
        });
      }
    }
  })
  .transform(({ correctOptionId, ...question }) => ({
    ...question,
    correctOptionIds: question.correctOptionIds ?? [correctOptionId!],
  }));

export const quizQuestionSchema = quizQuestionInputSchema;

export const quizExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  questions: z.array(quizQuestionSchema).min(1).max(100),
}).superRefine((config, context) => {
  const semanticKeys = config.questions.map((question) => {
    const correct = new Set(question.correctOptionIds);
    const options = question.options
      .map((option) => `${option.label.normalize("NFC").trim().replace(/\s+/g, " ")}:${correct.has(option.id)}`)
      .sort();
    return `${question.prompt.normalize("NFC").trim().replace(/\s+/g, " ")}|${options.join("|")}`;
  });
  if (new Set(semanticKeys).size !== semanticKeys.length) {
    context.addIssue({ code: "custom", path: ["questions"], message: "Eine vollständige Quizfrage darf innerhalb derselben Übung nicht doppelt vorkommen." });
  }
});

const lueckentextPartSchema = z.object({ type: z.literal("text"), text: z.string().max(10_000) });
const lueckentextGapSchema = z.object({
  type: z.literal("gap"),
  id: z.string().min(1).max(120).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  answer: z.string().trim().min(1).max(240),
  options: z.array(z.string().trim().min(1).max(240)).min(2).max(12).optional(),
});
export const lueckentextExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  parts: z.array(z.union([lueckentextPartSchema, lueckentextGapSchema])).min(3).max(300),
}).superRefine((config, context) => {
  const gaps = config.parts.filter((part): part is z.infer<typeof lueckentextGapSchema> => part.type === "gap");
  if (!gaps.length) context.addIssue({ code: "custom", path: ["parts"], message: "Der Lückentext benötigt mindestens eine Lücke." });
  if (new Set(gaps.map(({ id }) => id)).size !== gaps.length) context.addIssue({ code: "custom", path: ["parts"], message: "Jede Lücke benötigt eine eindeutige Kennung." });
  gaps.forEach((gap, index) => {
    if (gap.options && (!gap.options.includes(gap.answer) || new Set(gap.options).size !== gap.options.length)) {
      context.addIssue({ code: "custom", path: ["parts", index], message: "Auswahlmöglichkeiten müssen die richtige Antwort genau einmal enthalten." });
    }
  });
});

export const rechenmauerExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  count: z.number().int().min(1).max(20).default(5),
  height: z.union([z.literal(3), z.literal(4)]).default(3),
  maxResult: z.number().int().min(10).max(100_000).default(20),
  operation: z.literal("addition").default("addition"),
  basisVisible: z.literal(true).default(true),
});

export const rechenmauerRunSettingsSchema = z.object({
  count: z.number().int().min(1).max(20),
  height: z.union([z.literal(3), z.literal(4)]),
  maxResult: z.number().int().min(10).max(100_000),
});

const reimePairSchema = z.object({
  id: z.string().min(1).max(120),
  first: z.string().trim().min(1).max(120).regex(/^\p{L}+(?:-\p{L}+)*$/u),
  second: z.string().trim().min(1).max(120).regex(/^\p{L}+(?:-\p{L}+)*$/u),
  rhymes: z.boolean(),
});

export const reimeExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  pairs: z.array(reimePairSchema).min(2).max(1_000),
}).superRefine((config, context) => {
  if (!config.pairs.some((pair) => pair.rhymes)) {
    context.addIssue({ code: "custom", path: ["pairs"], message: "Mindestens ein Reimpaar ist erforderlich." });
  }
  if (!config.pairs.some((pair) => !pair.rhymes)) {
    context.addIssue({ code: "custom", path: ["pairs"], message: "Mindestens ein Nicht-Reimpaar ist erforderlich." });
  }
  if (new Set(config.pairs.map((pair) => pair.id)).size !== config.pairs.length) {
    context.addIssue({ code: "custom", path: ["pairs"], message: "Jedes Wortpaar benötigt eine eindeutige Kennung." });
  }
});

export const reimeRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
});

const zeitformSchema = z.enum(["praesens", "praeteritum", "perfekt", "plusquamperfekt", "futur1", "futur2"]);
const zeitformenSchwierigkeitSchema = z.enum(["leicht", "mittel", "schwer"]);
const zeitformenSentenceSchema = z.object({
  id: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  text: z.string().trim().min(4).max(500),
  tense: zeitformSchema,
  difficulty: zeitformenSchwierigkeitSchema,
});

export const zeitformenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  tenses: z.array(z.object({ id: zeitformSchema, label: z.string().trim().min(1).max(80) })).length(6),
  defaultTenses: z.array(zeitformSchema).min(2).max(6),
  defaultDifficulty: zeitformenSchwierigkeitSchema.default("mittel"),
  sentences: z.array(zeitformenSentenceSchema).min(2).max(5_000),
}).superRefine((config, context) => {
  if (new Set(config.tenses.map(({ id }) => id)).size !== config.tenses.length) context.addIssue({ code: "custom", path: ["tenses"], message: "Jede Zeitform darf nur einmal verfügbar sein." });
  if (new Set(config.defaultTenses).size !== config.defaultTenses.length) context.addIssue({ code: "custom", path: ["defaultTenses"], message: "Die Standardauswahl darf keine Zeitform doppelt enthalten." });
  const available = new Set(config.tenses.map(({ id }) => id));
  if (config.defaultTenses.some((tense) => !available.has(tense))) context.addIssue({ code: "custom", path: ["defaultTenses"], message: "Die Standardauswahl darf nur verfügbare Zeitformen enthalten." });
  if (new Set(config.sentences.map(({ id }) => id)).size !== config.sentences.length) context.addIssue({ code: "custom", path: ["sentences"], message: "Jeder Satz benötigt eine eindeutige Kennung." });
  if (config.sentences.some((sentence) => !available.has(sentence.tense))) context.addIssue({ code: "custom", path: ["sentences"], message: "Jeder Satz muss zu einer verfügbaren Zeitform gehören." });
  if (config.defaultTenses.some((tense) => !config.sentences.some((sentence) => sentence.tense === tense))) context.addIssue({ code: "custom", path: ["defaultTenses"], message: "Für jede Standardzeitform muss mindestens ein Satz vorhanden sein." });
});

export const zeitformenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  tenses: z.array(zeitformSchema).min(2).max(6),
  difficulty: zeitformenSchwierigkeitSchema,
});

const zeitformenKonjugierenVerbSchema = z.object({
  id: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  infinitive: z.string().trim().min(2).max(120),
  auxiliary: z.enum(["haben", "sein"]),
  participle: z.string().trim().min(2).max(160),
  present: z.array(z.string().trim().min(1).max(160)).length(6),
  preterite: z.array(z.string().trim().min(1).max(160)).length(6),
});

export const zeitformenKonjugierenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  tenses: z.array(z.object({ id: zeitformSchema, label: z.string().trim().min(1).max(80) })).length(6),
  defaultTenses: z.array(zeitformSchema).min(1).max(6),
  verbs: z.array(zeitformenKonjugierenVerbSchema).min(1).max(5_000),
}).superRefine((config, context) => {
  if (new Set(config.tenses.map(({ id }) => id)).size !== config.tenses.length) context.addIssue({ code: "custom", path: ["tenses"], message: "Jede Zeitform darf nur einmal verfügbar sein." });
  if (new Set(config.defaultTenses).size !== config.defaultTenses.length) context.addIssue({ code: "custom", path: ["defaultTenses"], message: "Die Standardauswahl darf keine Zeitform doppelt enthalten." });
  const available = new Set(config.tenses.map(({ id }) => id));
  if (config.defaultTenses.some((tense) => !available.has(tense))) context.addIssue({ code: "custom", path: ["defaultTenses"], message: "Die Standardauswahl darf nur verfügbare Zeitformen enthalten." });
  if (new Set(config.verbs.map(({ id }) => id)).size !== config.verbs.length) context.addIssue({ code: "custom", path: ["verbs"], message: "Jedes Verb benötigt eine eindeutige Kennung." });
  if (new Set(config.verbs.map(({ infinitive }) => infinitive.toLocaleLowerCase("de-DE"))).size !== config.verbs.length) context.addIssue({ code: "custom", path: ["verbs"], message: "Ein Verb darf nur einmal in der Verbquelle vorkommen." });
});

export const zeitformenKonjugierenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  tenses: z.array(zeitformSchema).min(1).max(6),
});

const spellingChoiceCategorySchema = z.object({
  id: z.string().trim().min(1).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  label: z.string().trim().min(1).max(120),
});

const spellingChoiceItemSchema = z.object({
  id: z.string().trim().min(1).max(160),
  correct: z.string().trim().min(1).max(120).regex(/^\p{L}+(?:-\p{L}+)*$/u),
  incorrect: z.string().trim().min(1).max(120).regex(/^\p{L}+(?:-\p{L}+)*$/u),
  lemma: z.string().trim().min(1).max(120),
  category: z.string().trim().min(1).max(80),
  role: z.enum(["target_rule_word", "contrast_word"]),
  vowelIndex: z.number().int().min(0).max(119).optional(),
  hint: z.string().trim().min(1).max(1_000),
});

const spellingChoiceConfigShape = {
  instructions: z.string().min(1).max(2_000),
  defaultCount: z.number().int().min(5).max(30).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  categories: z.array(spellingChoiceCategorySchema).min(1).max(30),
  defaultCategories: z.array(z.string().trim().min(1).max(80)).min(1).max(30).optional(),
  items: z.array(spellingChoiceItemSchema).min(2).max(5_000),
};

function spellingChoiceRefinement(config: { categories: Array<{ id: string }>; items: Array<{ id: string; correct: string; incorrect: string; category: string; role: string }> }, context: z.RefinementCtx) {
  const categoryIds = new Set(config.categories.map(({ id }) => id));
  if (categoryIds.size !== config.categories.length) context.addIssue({ code: "custom", path: ["categories"], message: "Jede Kategorie benötigt eine eindeutige Kennung." });
  if (new Set(config.items.map(({ id }) => id)).size !== config.items.length) context.addIssue({ code: "custom", path: ["items"], message: "Jeder Eintrag benötigt eine eindeutige Kennung." });
  const defaultCategories = (config as { defaultCategories?: string[] }).defaultCategories;
  if (defaultCategories && (new Set(defaultCategories).size !== defaultCategories.length || defaultCategories.some((category) => !categoryIds.has(category)))) {
    context.addIssue({ code: "custom", path: ["defaultCategories"], message: "Die Standardauswahl darf nur vorhandene Kategorien enthalten." });
  }
  for (const [index, item] of config.items.entries()) {
    if (!categoryIds.has(item.category)) context.addIssue({ code: "custom", path: ["items", index, "category"], message: "Der Eintrag verweist auf keine vorhandene Kategorie." });
    if (item.correct.toLocaleLowerCase("de-DE") === item.incorrect.toLocaleLowerCase("de-DE")) context.addIssue({ code: "custom", path: ["items", index], message: "Richtige und falsche Schreibweise müssen verschieden sein." });
  }
  if (!config.items.some(({ role }) => role === "target_rule_word") || !config.items.some(({ role }) => role === "contrast_word")) {
    context.addIssue({ code: "custom", path: ["items"], message: "Für die Wortdiagnostik werden Ziel- und Kontrastwörter benötigt." });
  }
}

export const dehnungsHExerciseConfigSchema = z.object(spellingChoiceConfigShape).superRefine(spellingChoiceRefinement).superRefine((config, context) => {
  if (config.defaultCount > 20) context.addIssue({ code: "custom", path: ["defaultCount"], message: "Dehnungs-h unterstützt höchstens 20 Aufgaben pro Durchlauf." });
});

export const kurzeLangeVokaleExerciseConfigSchema = z.object({
  ...spellingChoiceConfigShape,
  syllableColorsDefault: z.boolean().default(false),
  shortVowelHighlightDefault: z.boolean().default(false),
  longVowelHighlightDefault: z.boolean().default(false),
}).superRefine(spellingChoiceRefinement);

const spellingChoiceRunSettingsShape = {
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(30),
  durationSeconds: z.number().int().min(60).max(900),
  categories: z.array(z.string().trim().min(1).max(80)).min(1).max(30),
};

export const dehnungsHRunSettingsSchema = z.object(spellingChoiceRunSettingsShape).superRefine((settings, context) => {
  if (settings.count > 20) context.addIssue({ code: "custom", path: ["count"], message: "Dehnungs-h unterstützt höchstens 20 Aufgaben pro Durchlauf." });
});

export const kurzeLangeVokaleRunSettingsSchema = z.object({
  ...spellingChoiceRunSettingsShape,
  syllableColors: z.boolean(),
  shortVowelHighlight: z.boolean(),
  longVowelHighlight: z.boolean(),
});

const morphemStammItemSchema = z.object({
  id: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  before: z.string().max(500),
  after: z.string().max(500),
  targetWord: z.string().trim().min(2).max(160),
  lemma: z.string().trim().min(1).max(160),
  graphemes: z.tuple([z.enum(["ä", "äu", "Ä", "Äu"]), z.enum(["e", "eu", "E", "Eu"])]),
  expectedGrapheme: z.enum(["ä", "äu", "Ä", "Äu"]),
  distractors: z.array(z.string().trim().min(1).max(160)).min(2).max(6),
  distractorTypes: z.array(z.enum(["visual_neighbor", "phonetic_neighbor", "semantic_neighbor", "stem_confusion"])).min(2).max(6),
  difficulty: z.enum(["1", "2", "3"]),
  graphemePattern: z.literal("umlaut_derivation"),
  morphemContext: z.enum(["morphemic_root_validation", "prefix_validation", "suffix_validation"]),
}).superRefine((item, context) => {
  if (item.graphemes[0] !== item.expectedGrapheme) context.addIssue({ code: "custom", path: ["expectedGrapheme"], message: "Das erwartete Graphem muss zur ersten Graphemoption passen." });
  const expected = item.expectedGrapheme.toLocaleLowerCase("de-DE");
  if ((expected === "ä" && item.graphemes[1].toLocaleLowerCase("de-DE") !== "e") || (expected === "äu" && item.graphemes[1].toLocaleLowerCase("de-DE") !== "eu")) context.addIssue({ code: "custom", path: ["graphemes"], message: "Erlaubt sind nur ä/e und äu/eu." });
  if (item.distractorTypes.length !== item.distractors.length) context.addIssue({ code: "custom", path: ["distractorTypes"], message: "Jeder Distraktor benötigt genau einen Distraktortyp." });
  const stem = item.lemma.toLocaleLowerCase("de-DE");
  if (expected === "ä" && !stem.includes("a")) context.addIssue({ code: "custom", path: ["lemma"], message: "Für ä muss das Stammwort ein a enthalten." });
  if (expected === "äu" && !stem.includes("au")) context.addIssue({ code: "custom", path: ["lemma"], message: "Für äu muss das Stammwort ein au enthalten." });
});

export const morphemStammExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultCount: z.number().int().min(5).max(40).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  items: z.array(morphemStammItemSchema).min(5).max(5_000),
}).superRefine((config, context) => {
  if (new Set(config.items.map(({ id }) => id)).size !== config.items.length) context.addIssue({ code: "custom", path: ["items"], message: "Jedes Morphem-Item benötigt eine eindeutige Kennung." });
});

export const morphemStammRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(40),
  durationSeconds: z.number().int().min(60).max(900),
});

const morphemZerlegungItemSchema = z.object({
  id: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/),
  word: z.string().trim().min(2).max(160).regex(/^\p{L}+(?:-\p{L}+)*$/u),
  lemma: z.string().trim().min(1).max(160),
  prefix: z.string().max(80),
  stem: z.string().min(1).max(160),
  suffix: z.string().max(80),
  syllableSegments: z.tuple([z.string(), z.string(), z.string()]).optional(),
  difficulty: z.enum(["1", "2", "3"]),
}).superRefine((item, context) => {
  if (`${item.prefix}${item.stem}${item.suffix}` !== item.word) {
    context.addIssue({ code: "custom", path: ["stem"], message: "Präfix, Stamm und Suffix müssen zusammen exakt das Wort ergeben." });
  }
  if (item.syllableSegments && item.syllableSegments.join("") !== item.word) {
    context.addIssue({ code: "custom", path: ["syllableSegments"], message: "Die Silbenalternative muss zusammen exakt das Wort ergeben." });
  }
});

export const morphemZerlegungExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultCount: z.number().int().min(5).max(40).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  items: z.array(morphemZerlegungItemSchema).min(5).max(5_000),
}).superRefine((config, context) => {
  if (new Set(config.items.map(({ id }) => id)).size !== config.items.length) {
    context.addIssue({ code: "custom", path: ["items"], message: "Jedes Morphem-Zerlegungsitem benötigt eine eindeutige Kennung." });
  }
  if (new Set(config.items.map(({ word }) => word.toLocaleLowerCase("de-DE"))).size !== config.items.length) {
    context.addIssue({ code: "custom", path: ["items"], message: "Die Wortquelle darf kein Wort doppelt enthalten." });
  }
});

export const morphemZerlegungRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(40),
  durationSeconds: z.number().int().min(60).max(900),
});

const wordartSchema = z.enum(["Artikel", "Nomen", "Adjektiv", "Verb", "Adverb", "Pronomen", "Präposition", "Konjunktion", "Interjektion", "Numerale", "Partikel"]);
const wordartenContextSchema = z.enum(["standard", "capitalized_distractor", "nominalized", "adverbial_use"]);
const wordartenTokenSchema = z.object({
  id: z.string().trim().min(1).max(120).regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/),
  surface: z.string().trim().min(1).max(120).regex(/^\p{L}+(?:-\p{L}+)*$/u),
  lemma: z.string().trim().min(1).max(120),
  expectedPos: wordartSchema,
  syntacticContext: wordartenContextSchema.default("standard"),
  wordType: z.enum(["noun", "verb", "adjective", "proper_name", "nominalized_verb", "nominalized_adjective", "time_noun", "quantity_word", "function_word", "other"]),
  after: z.string().max(12).optional(),
});
const wordartenSentenceSchema = z.object({
  id: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  tokens: z.array(wordartenTokenSchema).min(2).max(40),
  suffix: z.string().max(12).default("."),
});
const wordartenCategorySchema = z.object({ id: wordartSchema, label: z.string().trim().min(1).max(80), base: z.boolean().default(false) });

export const wortartenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  mode: z.enum(["fixed", "generator"]).default("fixed"),
  defaultCount: z.number().int().min(1).max(30).default(5),
  defaultRunMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  categories: z.array(wordartenCategorySchema).min(4).max(11),
  defaultCategories: z.array(wordartSchema).min(4).max(11),
  defaultIncludeNominalizations: z.boolean().default(false),
  sentences: z.array(wordartenSentenceSchema).min(1).max(1_000),
}).superRefine((config, context) => {
  const categoryIds = new Set(config.categories.map(({ id }) => id));
  if (categoryIds.size !== config.categories.length) context.addIssue({ code: "custom", path: ["categories"], message: "Jede Wortart darf nur einmal vorkommen." });
  if (["Artikel", "Nomen", "Adjektiv", "Verb"].some((category) => !categoryIds.has(category as z.infer<typeof wordartSchema>))) {
    context.addIssue({ code: "custom", path: ["categories"], message: "Artikel, Nomen, Adjektiv und Verb sind die festen Basiswortarten." });
  }
  if (config.defaultCategories.some((category) => !categoryIds.has(category)) || new Set(config.defaultCategories).size !== config.defaultCategories.length) {
    context.addIssue({ code: "custom", path: ["defaultCategories"], message: "Die Standardauswahl darf nur vorhandene Wortarten enthalten." });
  }
  if (["Artikel", "Nomen", "Adjektiv", "Verb"].some((category) => !config.defaultCategories.includes(category as z.infer<typeof wordartSchema>))) {
    context.addIssue({ code: "custom", path: ["defaultCategories"], message: "Artikel, Nomen, Adjektiv und Verb müssen immer ausgewählt sein." });
  }
  const sentenceIds = new Set<string>();
  for (const [sentenceIndex, sentence] of config.sentences.entries()) {
    if (sentenceIds.has(sentence.id)) context.addIssue({ code: "custom", path: ["sentences", sentenceIndex, "id"], message: "Jeder Satz braucht eine eindeutige Kennung." });
    sentenceIds.add(sentence.id);
    const tokenIds = new Set<string>();
    for (const [tokenIndex, token] of sentence.tokens.entries()) {
      if (tokenIds.has(token.id)) context.addIssue({ code: "custom", path: ["sentences", sentenceIndex, "tokens", tokenIndex, "id"], message: "Jedes Wort im Satz braucht eine eindeutige Kennung." });
      tokenIds.add(token.id);
      if (!categoryIds.has(token.expectedPos)) context.addIssue({ code: "custom", path: ["sentences", sentenceIndex, "tokens", tokenIndex, "expectedPos"], message: "Die erwartete Wortart ist nicht als Kategorie verfügbar." });
    }
  }
});

export const wortartenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(1).max(30),
  durationSeconds: z.number().int().min(60).max(900),
  categories: z.array(wordartSchema).min(4).max(11),
  includeNominalizations: z.boolean().default(false),
});

const grosskleinRuleSchema = z.enum(["standard", "nominalisierte-verben", "nominalisierte-adjektive", "signalwoerter", "zeit-mengenangaben", "eigennamen"]);
const grosskleinContextSchema = z.enum(["sentence_start", "standard_noun", "signal_nominalization", "attributive_adjective", "predicate_verb", "proper_name", "time_quantity", "other"]);
const grosskleinTokenSchema = z.object({ id: z.string().trim().min(1).max(120).regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/), surface: z.string().trim().min(1).max(120).regex(/^\p{L}+$/u), lemma: z.string().trim().min(1).max(120), expectedCapitalized: z.boolean(), context: grosskleinContextSchema, after: z.string().max(12).optional() });
const grosskleinSentenceSchema = z.object({ id: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/), rule: grosskleinRuleSchema, tokens: z.array(grosskleinTokenSchema).min(2).max(40), suffix: z.string().max(12).default(".") });
export const grosskleinExerciseConfigSchema = z.object({ instructions: z.string().min(1).max(2_000), defaultCount: z.number().int().min(1).max(30).default(10), defaultMode: z.enum(["count", "time"]).default("count"), defaultDurationSeconds: z.number().int().min(60).max(900).default(180), rules: z.array(z.object({ id: grosskleinRuleSchema, label: z.string().min(1).max(80) })).min(1), defaultRules: z.array(grosskleinRuleSchema).min(1), sentences: z.array(grosskleinSentenceSchema).min(1).max(1_000) }).superRefine((config, context) => {
  const rules = new Set(config.rules.map(({ id }) => id));
  if (new Set(config.defaultRules).size !== config.defaultRules.length || config.defaultRules.some((rule) => !rules.has(rule))) context.addIssue({ code: "custom", path: ["defaultRules"], message: "Die Standardregeln müssen in der verfügbaren Regelauswahl liegen." });
  const sentenceIds = new Set<string>();
  for (const [sentenceIndex, sentence] of config.sentences.entries()) {
    if (sentenceIds.has(sentence.id)) context.addIssue({ code: "custom", path: ["sentences", sentenceIndex, "id"], message: "Jeder Satz braucht eine eindeutige Kennung." });
    sentenceIds.add(sentence.id);
    if (!rules.has(sentence.rule)) context.addIssue({ code: "custom", path: ["sentences", sentenceIndex, "rule"], message: "Der Regelbereich ist nicht verfügbar." });
  }
});
export const grosskleinRunSettingsSchema = z.object({ mode: z.enum(["count", "time"]), count: z.number().int().min(1).max(30), durationSeconds: z.number().int().min(60).max(900), rules: z.array(grosskleinRuleSchema).min(1).max(6) });
export const matheExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultRange: matheZahlenraumSchema.default("0-20"),
  defaultOperations: z.array(matheOperationSchema).min(1).max(4).default(["addition"]),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
}).superRefine((config, context) => {
  if (new Set(config.defaultOperations).size !== config.defaultOperations.length) {
    context.addIssue({ code: "custom", path: ["defaultOperations"], message: "Jede Rechenart darf nur einmal vorausgewählt sein." });
  }
});
export const matheRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  range: matheZahlenraumSchema,
  operations: z.array(matheOperationSchema).min(1).max(4),
}).superRefine((settings, context) => {
  if (new Set(settings.operations).size !== settings.operations.length) {
    context.addIssue({ code: "custom", path: ["operations"], message: "Jede Rechenart darf nur einmal ausgewählt sein." });
  }
});


export const stellenwertAdditionPhaseSchema = z.enum(["move_tens", "observe_material", "mental_short"]);
export const stellenwertAdditionCountSchema = z.union([z.literal(5), z.literal(10)]);
export const stellenwertAdditionExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultPhase: stellenwertAdditionPhaseSchema.default("move_tens"),
  defaultCount: stellenwertAdditionCountSchema.default(5),
});
export const stellenwertAdditionRunSettingsSchema = z.object({
  phase: stellenwertAdditionPhaseSchema,
  count: stellenwertAdditionCountSchema,
});

export const rundenZahlenraumSchema = z.enum(["0-100", "100-1000", "1000-1000000"]);
export const rundenStelleSchema = z.enum(["ones", "tens", "hundreds", "thousands", "ten-thousands", "hundred-thousands"]);
export const rundenStellenNachZahlenraum = {
  "0-100": ["ones", "tens", "hundreds"],
  "100-1000": ["ones", "tens", "hundreds", "thousands"],
  "1000-1000000": ["ones", "tens", "hundreds", "thousands", "ten-thousands", "hundred-thousands"],
} as const satisfies Record<z.infer<typeof rundenZahlenraumSchema>, readonly z.infer<typeof rundenStelleSchema>[]>;

function validateRundenSettings(
  value: { defaultRange?: z.infer<typeof rundenZahlenraumSchema>; range?: z.infer<typeof rundenZahlenraumSchema>; defaultPlaces?: z.infer<typeof rundenStelleSchema>[]; places?: z.infer<typeof rundenStelleSchema>[]; defaultDecimals?: boolean; decimals?: boolean },
  context: z.RefinementCtx,
) {
  const range = value.defaultRange ?? value.range!;
  const places = value.defaultPlaces ?? value.places!;
  const decimals = value.defaultDecimals ?? value.decimals!;
  if (new Set(places).size !== places.length) {
    context.addIssue({ code: "custom", path: [value.defaultPlaces ? "defaultPlaces" : "places"], message: "Jede Rundungsstelle darf nur einmal ausgewählt sein." });
  }
  const allowed = new Set<string>(rundenStellenNachZahlenraum[range]);
  if (places.some((place) => !allowed.has(place))) {
    context.addIssue({ code: "custom", path: [value.defaultPlaces ? "defaultPlaces" : "places"], message: "Die Rundungsstelle passt nicht zum gewählten Zahlenraum." });
  }
  if (!decimals && places.includes("ones")) {
    context.addIssue({ code: "custom", path: [value.defaultPlaces ? "defaultPlaces" : "places"], message: "Die Einerstelle ist nur für Dezimalzahlen verfügbar." });
  }
}

export const rundenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultRange: rundenZahlenraumSchema.default("0-100"),
  defaultPlaces: z.array(rundenStelleSchema).min(1).max(6).default(["tens", "hundreds"]),
  defaultDecimals: z.boolean().default(false),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
}).superRefine(validateRundenSettings);

export const rundenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  range: rundenZahlenraumSchema,
  places: z.array(rundenStelleSchema).min(1).max(6),
  decimals: z.boolean(),
}).superRefine(validateRundenSettings);

export const zahlenZerlegenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultRange: matheZahlenraumSchema.default("0-20"),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
});
export const zahlenZerlegenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  range: matheZahlenraumSchema,
});

export const froschHupfenRangeSchema = z.enum(["1-10", "1-20"]);
export const froschHupfenOperationSchema = z.enum(["mixed", "addition", "subtraction"]);
export const froschHupfenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultRange: froschHupfenRangeSchema.default("1-10"),
  defaultOperation: froschHupfenOperationSchema.default("mixed"),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
});
export const froschHupfenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  range: froschHupfenRangeSchema,
  operation: froschHupfenOperationSchema,
});
export const waageZahlenraumSchema = z.enum(["1-5", "1-10", "1-20"]);
export const waageSchwierigkeitSchema = z.enum(["leicht", "mittel", "schwer"]);
export const waageAnzahlSchema = z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]);
export const waageExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultRange: waageZahlenraumSchema.default("1-10"),
  defaultDifficulty: waageSchwierigkeitSchema.default("leicht"),
  defaultCount: waageAnzahlSchema.default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
});
export const waageRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: waageAnzahlSchema,
  durationSeconds: z.number().int().min(60).max(900),
  range: waageZahlenraumSchema,
  difficulty: waageSchwierigkeitSchema,
});
export const mengenMatchAnordnungSchema = z.enum(["geordnet", "gestreut"]);
export const mengenMatchGroessenSchema = z.enum(["einheitlich", "variierend"]);
export const mengenMatchExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultRange: waageZahlenraumSchema.default("1-10"),
  defaultArrangement: mengenMatchAnordnungSchema.default("geordnet"),
  defaultSizes: mengenMatchGroessenSchema.default("einheitlich"),
  defaultCount: waageAnzahlSchema.default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
});
export const mengenMatchRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: waageAnzahlSchema,
  durationSeconds: z.number().int().min(60).max(900),
  range: waageZahlenraumSchema,
  arrangement: mengenMatchAnordnungSchema,
  sizes: mengenMatchGroessenSchema,
});
export const zaehlenTippenZahlenraumSchema = z.enum(["1-5", "1-10", "1-20"]);
export const zaehlenTippenSchwierigkeitSchema = z.enum(["leicht", "mittel", "schwer"]);
export const zaehlenTippenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultRange: zaehlenTippenZahlenraumSchema.default("1-10"),
  defaultDifficulty: zaehlenTippenSchwierigkeitSchema.default("mittel"),
  defaultCount: waageAnzahlSchema.default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
});
export const zaehlenTippenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: waageAnzahlSchema,
  durationSeconds: z.number().int().min(60).max(900),
  range: zaehlenTippenZahlenraumSchema,
  difficulty: zaehlenTippenSchwierigkeitSchema,
});
const sortierspielObjectSchema = z.object({
  id: z.string().trim().min(1).max(120).regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/),
  emoji: z.string().trim().min(1).max(32),
});
const sortierspielCategorySchema = z.object({
  id: z.string().trim().min(1).max(120).regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/),
  label: z.string().trim().min(1).max(80),
  objects: z.array(sortierspielObjectSchema).min(1).max(40),
});
export const sortierspielExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  mode: z.enum(["fixed", "random"]).default("fixed"),
  defaultCategoryCount: z.number().int().min(2).max(4).default(2),
  defaultObjectsPerCategory: z.number().int().min(3).max(6).default(5),
  categories: z.array(sortierspielCategorySchema).min(2).max(8),
}).superRefine((config, context) => {
  if (new Set(config.categories.map(({ id }) => id)).size !== config.categories.length) {
    context.addIssue({ code: "custom", path: ["categories"], message: "Jede Gruppe braucht eine eindeutige Kennung." });
  }
  const objectIds = config.categories.flatMap(({ objects }) => objects.map(({ id }) => id));
  if (new Set(objectIds).size !== objectIds.length) {
    context.addIssue({ code: "custom", path: ["categories"], message: "Jedes Objekt braucht eine eindeutige Kennung." });
  }
  const semanticObjects = config.categories.flatMap((category) => category.objects.map((object) => `${object.emoji.normalize("NFC")}|${category.label.normalize("NFC").trim().replace(/\s+/g, " ")}`));
  if (new Set(semanticObjects).size !== semanticObjects.length) {
    context.addIssue({ code: "custom", path: ["categories"], message: "Dasselbe Sortierobjekt mit derselben Zielgruppe darf innerhalb einer Übung nicht doppelt vorkommen." });
  }
  if (config.mode === "random") {
    if (config.defaultCategoryCount > config.categories.length) {
      context.addIssue({ code: "custom", path: ["defaultCategoryCount"], message: "Es sind nicht genügend Gruppen vorhanden." });
    }
    if (config.categories.some(({ objects }) => objects.length < config.defaultObjectsPerCategory)) {
      context.addIssue({ code: "custom", path: ["defaultObjectsPerCategory"], message: "Jede Gruppe benötigt genügend Objekte für die Standardauswahl." });
    }
  }
});
export const sortierspielRunSettingsSchema = z.object({
  categoryCount: z.number().int().min(2).max(4),
  objectsPerCategory: z.number().int().min(3).max(6),
});
export const schriftlichRechnenOperationSchema = z.enum(["addition", "subtraction", "multiplication", "division"]);
export const schriftlichRechnenMultiplicationMethodSchema = z.enum(["standard", "place-value"]);
export const schriftlichRechnenMultiplicationProfileSchema = z.enum(["2x1", "3x1", "4x1", "2x2", "3x2", "4x2"]);
export const schriftlichRechnenDivisionProfileSchema = z.enum(["3:1", "4:1", "5:1"]);
export const schriftlichRechnenExerciseConfigSchema = z.object({
  instructions: z.string().min(1).max(2_000),
  defaultOperations: z.array(schriftlichRechnenOperationSchema).min(1).max(4).default(["addition", "subtraction", "multiplication", "division"]),
  defaultPlaces: z.union([z.literal(3), z.literal(4), z.literal(5)]).default(4),
  defaultMultiplicationMethod: schriftlichRechnenMultiplicationMethodSchema.default("standard"),
  defaultMultiplicationProfile: schriftlichRechnenMultiplicationProfileSchema.default("3x2"),
  defaultDivisionProfile: schriftlichRechnenDivisionProfileSchema.default("4:1"),
  defaultImmediateFeedback: z.boolean().default(false),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
}).superRefine((config, context) => {
  if (new Set(config.defaultOperations).size !== config.defaultOperations.length) {
    context.addIssue({ code: "custom", path: ["defaultOperations"], message: "Jede Rechenart darf nur einmal vorausgewählt sein." });
  }
});
export const schriftlichRechnenRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  operations: z.array(schriftlichRechnenOperationSchema).min(1).max(4),
  places: z.union([z.literal(3), z.literal(4), z.literal(5)]),
  multiplicationMethod: schriftlichRechnenMultiplicationMethodSchema.default("standard"),
  multiplicationProfile: schriftlichRechnenMultiplicationProfileSchema,
  divisionProfile: schriftlichRechnenDivisionProfileSchema,
  immediateFeedback: z.boolean(),
}).superRefine((settings, context) => {
  if (new Set(settings.operations).size !== settings.operations.length) {
    context.addIssue({ code: "custom", path: ["operations"], message: "Jede Rechenart darf nur einmal ausgewählt sein." });
  }
});
const satzgliedRoleSchema = z.enum(["Subjekt", "Prädikat", "Dativobjekt", "Akkusativobjekt", "Präpositionalobjekt", "Adverbiale Bestimmung"]);
const satzgliederVariantSchema = z.enum(["svo_canonical", "ovs_fronted_object", "adv_fronted", "split_predicate", "dative_object", "prepositional_object"]);
const satzgliederChunkSchema = z.object({ id: z.string().min(1).max(120).regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/), text: z.string().min(1).max(200), expectedRole: satzgliedRoleSchema, caseSignal: z.enum(["dative", "accusative", "prepositional"]).optional(), predicatePart: z.enum(["front", "back"]).optional() });
const satzgliederSentenceSchema = z.object({ id: z.string().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/), variant: satzgliederVariantSchema, frontedRole: satzgliedRoleSchema.optional(), chunks: z.array(satzgliederChunkSchema).min(2).max(12), suffix: z.string().max(12).default(".") });
export const satzgliederExerciseConfigSchema = z.object({ instructions: z.string().min(1).max(2_000), mode: z.enum(["fixed", "generator"]).default("fixed"), defaultCount: z.number().int().min(1).max(30).default(10), defaultMode: z.enum(["count", "time"]).default("count"), defaultDurationSeconds: z.number().int().min(60).max(900).default(180), roles: z.array(z.object({ id: satzgliedRoleSchema, label: z.string(), base: z.boolean() })).min(4), defaultRoles: z.array(satzgliedRoleSchema).min(4), sentences: z.array(satzgliederSentenceSchema).min(1).max(1_000) }).superRefine((config, context) => { const roles = new Set(config.roles.map(({ id }) => id)); if (["Subjekt", "Prädikat", "Dativobjekt", "Akkusativobjekt"].some((role) => !config.defaultRoles.includes(role as z.infer<typeof satzgliedRoleSchema>))) context.addIssue({ code: "custom", path: ["defaultRoles"], message: "Die vier Basis-Satzglieder müssen aktiv sein." }); if (config.defaultRoles.some((role) => !roles.has(role))) context.addIssue({ code: "custom", path: ["defaultRoles"], message: "Die Standardauswahl ist ungültig." }); });
export const satzgliederRunSettingsSchema = z.object({ mode: z.enum(["count", "time"]), count: z.number().int().min(1).max(30), durationSeconds: z.number().int().min(60).max(900), roles: z.array(satzgliedRoleSchema).min(4).max(6) });

export const blitzlesenWordSchema = z.string().trim().min(1).max(120).regex(
  /^\p{L}+(?:-\p{L}+)*$/u,
  "Ein Blitzlesen-Eintrag darf nur Buchstaben und innere Bindestriche enthalten.",
);

const blitzlesenSyllableCountSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);

const blitzlesenMaterialGroupSchema = z.object({
  id: z.string().trim().min(1).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  label: z.string().trim().min(1).max(120),
  syllableColors: z.boolean().default(false),
  entries: z.array(blitzlesenWordSchema).min(1).max(5_000),
});

const blitzlesenMaterialBankSchema = z.object({
  id: z.string().trim().min(1).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  label: z.string().trim().min(1).max(120),
  itemLabelSingular: z.string().trim().min(1).max(40),
  itemLabelPlural: z.string().trim().min(1).max(40),
  selectionLabel: z.string().trim().min(1).max(80),
  selectionMode: z.enum(["multiple", "single"]),
  defaultGroupIds: z.array(z.string().trim().min(1).max(80)).min(1).max(10),
  groups: z.array(blitzlesenMaterialGroupSchema).min(1).max(10),
}).superRefine((bank, context) => {
  const groupIds = bank.groups.map(({ id }) => id);
  if (new Set(groupIds).size !== groupIds.length) {
    context.addIssue({ code: "custom", path: ["groups"], message: "Jede Materialgruppe darf nur einmal vorkommen." });
  }
  if (new Set(bank.defaultGroupIds).size !== bank.defaultGroupIds.length || bank.defaultGroupIds.some((id) => !groupIds.includes(id))) {
    context.addIssue({ code: "custom", path: ["defaultGroupIds"], message: "Die Standardgruppen müssen zu dieser Materialbank gehören." });
  }
  if (bank.selectionMode === "single" && bank.defaultGroupIds.length !== 1) {
    context.addIssue({ code: "custom", path: ["defaultGroupIds"], message: "Bei einer Einzelauswahl muss genau eine Standardgruppe gesetzt sein." });
  }
  const normalizedEntries = bank.groups.flatMap(({ entries }) => entries.map((entry) => entry.toLocaleLowerCase("de-DE")));
  if (new Set(normalizedEntries).size !== normalizedEntries.length) {
    context.addIssue({ code: "custom", path: ["groups"], message: "Ein Eintrag darf innerhalb einer Materialbank nur einmal vorkommen." });
  }
});

export const blitzlesenExerciseConfigSchema = z.object({
  readingMaterials: readingMaterialsSchema.optional(),
  instructions: z.string().min(1).max(2_000),
  defaultDisplayDurationMs: z.number().int().min(50).max(9_000).default(500),
  syllableColorsDefault: z.boolean().default(false),
  syllableSelectionMode: z.enum(["multiple", "single"]).default("multiple"),
  selectableSyllableCounts: z.array(blitzlesenSyllableCountSchema).min(1).max(4).default([1, 2, 3, 4]),
  syllableColorCounts: z.array(blitzlesenSyllableCountSchema).max(4).default([1, 2, 3, 4]),
  words: z.array(blitzlesenWordSchema).max(5_000).default([]),
  defaultMaterialBankId: z.string().trim().min(1).max(80).optional(),
  materialBanks: z.array(blitzlesenMaterialBankSchema).max(10).default([]),
}).superRefine((config, context) => {
  if (config.words.length === 0 && config.materialBanks.length === 0) {
    context.addIssue({ code: "custom", path: ["words"], message: "Blitzlesen benötigt eine Wortliste oder Materialbanken." });
  }
  if (config.materialBanks.length > 0) {
    const bankIds = config.materialBanks.map(({ id }) => id);
    if (new Set(bankIds).size !== bankIds.length) {
      context.addIssue({ code: "custom", path: ["materialBanks"], message: "Jede Materialbank darf nur einmal vorkommen." });
    }
    if (!config.defaultMaterialBankId || !bankIds.includes(config.defaultMaterialBankId)) {
      context.addIssue({ code: "custom", path: ["defaultMaterialBankId"], message: "Die Standard-Materialbank muss vorhanden sein." });
    }
    const entryCount = config.materialBanks.reduce((sum, bank) => sum + bank.groups.reduce((groupSum, group) => groupSum + group.entries.length, 0), 0);
    if (entryCount > 5_000) {
      context.addIssue({ code: "custom", path: ["materialBanks"], message: "Blitzlesen darf insgesamt höchstens 5.000 Materialeinträge enthalten." });
    }
  } else if (config.defaultMaterialBankId) {
    context.addIssue({ code: "custom", path: ["defaultMaterialBankId"], message: "Ohne Materialbanken darf keine Standard-Materialbank gesetzt sein." });
  }
  const normalized = config.words.map((word) => word.toLocaleLowerCase("de-DE"));
  if (new Set(normalized).size !== normalized.length) {
    context.addIssue({ code: "custom", path: ["words"], message: "Die Wortliste darf keine Duplikate enthalten." });
  }
  if (new Set(config.selectableSyllableCounts).size !== config.selectableSyllableCounts.length) {
    context.addIssue({ code: "custom", path: ["selectableSyllableCounts"], message: "Jede auswählbare Silbenanzahl darf nur einmal vorkommen." });
  }
  if (new Set(config.syllableColorCounts).size !== config.syllableColorCounts.length) {
    context.addIssue({ code: "custom", path: ["syllableColorCounts"], message: "Jede Silbenanzahl für die Färbung darf nur einmal vorkommen." });
  }
  if (config.syllableColorCounts.some((count) => !config.selectableSyllableCounts.includes(count))) {
    context.addIssue({ code: "custom", path: ["syllableColorCounts"], message: "Silbenfärbung kann nur für auswählbare Silbenanzahlen angeboten werden." });
  }
});

const individualExerciseWordsSchema = z.array(blitzlesenWordSchema).min(1).max(5_000).superRefine((words, context) => {
  const normalized = words.map((word) => word.toLocaleLowerCase("de-DE"));
  if (new Set(normalized).size !== normalized.length) {
    context.addIssue({ code: "custom", message: "Die Wortliste darf keine Duplikate enthalten." });
  }
});

export const individualExerciseCreateInputSchema = z.object({
  readingMaterials: readingMaterialsSchema.optional(),
  exerciseType: z.enum(["blitzlesen", "laute-erkennen"]),
  lauteWords: lauteWordsSchema.optional(),
  title: z.string().trim().min(2).max(160),
  words: individualExerciseWordsSchema.optional(),
}).superRefine((value, context) => {
  if (value.exerciseType === "blitzlesen" ? !value.words : !value.lauteWords || value.lauteWords.some((word) => word.audioId)) context.addIssue({ code: "custom", message: "Die Wortliste passt nicht zum Aufgabentyp. Eigene Lautwörter besitzen keine Aufnahme." });
});

export const individualExerciseUpdateInputSchema = z.object({
  exerciseType: z.enum(["blitzlesen", "laute-erkennen"]).optional(),
  lauteWords: lauteWordsSchema.optional(),
  readingMaterials: readingMaterialsSchema.optional(),
  title: z.string().trim().min(2).max(160),
  words: individualExerciseWordsSchema.optional(),
}).superRefine((value, context) => {
  if ((!value.words && !value.lauteWords) || (value.words && value.lauteWords) || value.lauteWords?.some((word) => word.audioId)) context.addIssue({ code: "custom", message: "Genau eine vollständige Wortliste ohne eigene Audioverweise ist erforderlich." });
});

export const individualExerciseVisibilityInputSchema = z.object({
  visibility: z.enum(["PRIVATE", "ORGANIZATION"]),
});

export const individualExerciseAssignmentInputSchema = z.object({
  learnerIds: z.array(z.string().uuid()).min(1).max(100).superRefine((learnerIds, context) => {
    if (new Set(learnerIds).size !== learnerIds.length) {
      context.addIssue({ code: "custom", message: "Eine lernende Person darf nur einmal ausgewählt werden." });
    }
  }),
});

export const blitzlesenRunSettingsSchema = z.object({
  syllableCounts: z.array(blitzlesenSyllableCountSchema).max(4).default([]),
  materialBankId: z.string().trim().min(1).max(80).optional(),
  groupIds: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  wordCount: z.number().int().min(0).max(5_000),
  displayDurationMs: z.number().int().min(50).max(9_000),
  syllableColors: z.boolean(),
}).superRefine((settings, context) => {
  if (settings.materialBankId) {
    if (settings.groupIds.length === 0) context.addIssue({ code: "custom", path: ["groupIds"], message: "Mindestens eine Materialgruppe muss ausgewählt sein." });
    if (new Set(settings.groupIds).size !== settings.groupIds.length) context.addIssue({ code: "custom", path: ["groupIds"], message: "Jede Materialgruppe darf nur einmal ausgewählt sein." });
  } else if (settings.syllableCounts.length === 0) {
    context.addIssue({ code: "custom", path: ["syllableCounts"], message: "Mindestens eine Silbenanzahl muss ausgewählt sein." });
  }
});

const silbentrennungWordSchema = z.object({
  id: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/),
  surface: z.string().trim().min(2).max(120).regex(/^\p{L}+(?:-\p{L}+)*$/u),
  lemma: z.string().trim().min(2).max(120),
  syllables: z.array(z.string().trim().min(1).max(80).regex(/^\p{L}+$/u)).min(1).max(8),
});

const silbentrennungSyllableCountSchema = z.union([z.literal(2), z.literal(3), z.literal(4)]);

export const silbentrennungExerciseConfigSchema = z.object({
  readingMaterials: readingMaterialsSchema.optional(),
  instructions: z.string().trim().min(1).max(2_000),
  defaultCount: z.number().int().min(5).max(20).default(10),
  defaultMode: z.enum(["count", "time"]).default("count"),
  defaultDurationSeconds: z.number().int().min(60).max(900).default(180),
  defaultSyllableCounts: z.array(silbentrennungSyllableCountSchema).min(1).max(3).default([2, 3, 4]),
  defaultVowelMarking: z.boolean().default(false),
  defaultGraphemeHighlight: z.boolean().default(false),
  words: z.array(silbentrennungWordSchema).min(2).max(5_000),
}).superRefine((config, context) => {
  if (new Set(config.words.map(({ id }) => id)).size !== config.words.length) {
    context.addIssue({ code: "custom", path: ["words"], message: "Jedes Wort benötigt eine eindeutige Kennung." });
  }
  if (new Set(config.words.map(({ surface }) => surface.toLocaleLowerCase("de-DE"))).size !== config.words.length) {
    context.addIssue({ code: "custom", path: ["words"], message: "Die Wortquelle darf kein Wort doppelt enthalten." });
  }
  if (new Set(config.defaultSyllableCounts).size !== config.defaultSyllableCounts.length) {
    context.addIssue({ code: "custom", path: ["defaultSyllableCounts"], message: "Jede Silbenanzahl darf nur einmal gewählt werden." });
  }
  for (const [index, word] of config.words.entries()) {
    if (word.syllables.join("") !== word.surface) {
      context.addIssue({ code: "custom", path: ["words", index, "syllables"], message: "Die Lesesilben müssen das Wort vollständig und in derselben Schreibweise ergeben." });
    }
  }
  if (config.defaultSyllableCounts.some((count) => !config.words.some((word) => Math.min(4, word.syllables.length) === count))) {
    context.addIssue({ code: "custom", path: ["defaultSyllableCounts"], message: "Für jede Standardsilbenanzahl muss mindestens ein Wort vorhanden sein." });
  }
});

export const silbentrennungRunSettingsSchema = z.object({
  mode: z.enum(["count", "time"]),
  count: z.number().int().min(5).max(20),
  durationSeconds: z.number().int().min(60).max(900),
  syllableCounts: z.array(silbentrennungSyllableCountSchema).min(1).max(3),
  vowelMarking: z.boolean(),
  graphemeHighlight: z.boolean(),
}).superRefine((settings, context) => {
  if (new Set(settings.syllableCounts).size !== settings.syllableCounts.length) {
    context.addIssue({ code: "custom", path: ["syllableCounts"], message: "Jede Silbenanzahl darf nur einmal gewählt werden." });
  }
});

export const exerciseConfigRegistry = {
  "laute-erkennen": { schema: lauteExerciseConfigSchema, invalidMessage: "Die Laut-Konfiguration ist nicht gültig." },
  quiz: { schema: quizExerciseConfigSchema, invalidMessage: "Die Quiz-Konfiguration ist nicht gültig." },
  lueckentext: { schema: lueckentextExerciseConfigSchema, invalidMessage: "Die Lückentext-Konfiguration ist nicht gültig." },
  rechenmauer: { schema: rechenmauerExerciseConfigSchema, invalidMessage: "Die Rechenmauer-Konfiguration ist nicht gültig." },
  mathe: { schema: matheExerciseConfigSchema, invalidMessage: "Die Rechentraining-Konfiguration ist nicht gültig." },
  einmaleins: { schema: einmaleinsExerciseConfigSchema, invalidMessage: "Die Konfiguration des 1×1-Trainers ist nicht gültig." },
  "stellenwert-addition": { schema: stellenwertAdditionExerciseConfigSchema, invalidMessage: "Die Konfiguration für ‚Zehner zuerst addieren‘ ist nicht gültig." },
  transkodieren: { schema: transkodierenExerciseConfigSchema, invalidMessage: "Die Transkodieren-Konfiguration ist nicht gültig." },
  runden: { schema: rundenExerciseConfigSchema, invalidMessage: "Die Rundungs-Konfiguration ist nicht gültig." },
  "zahlen-zerlegen": { schema: zahlenZerlegenExerciseConfigSchema, invalidMessage: "Die Zahlen-zerlegen-Konfiguration ist nicht gültig." },
  "frosch-hupfen": { schema: froschHupfenExerciseConfigSchema, invalidMessage: "Die Frosch-Hüpfen-Konfiguration ist nicht gültig." },
  waage: { schema: waageExerciseConfigSchema, invalidMessage: "Die Waage-Konfiguration ist nicht gültig." },
  "mengen-match": { schema: mengenMatchExerciseConfigSchema, invalidMessage: "Die Mengen-Match-Konfiguration ist nicht gültig." },
  "zaehlen-tippen": { schema: zaehlenTippenExerciseConfigSchema, invalidMessage: "Die Zählen-und-Tippen-Konfiguration ist nicht gültig." },
  sortierspiel: { schema: sortierspielExerciseConfigSchema, invalidMessage: "Die Sortierspiel-Konfiguration ist nicht gültig." },
  "schriftlich-rechnen": { schema: schriftlichRechnenExerciseConfigSchema, invalidMessage: "Die Konfiguration für schriftliches Rechnen ist nicht gültig." },
  blitzlesen: { schema: blitzlesenExerciseConfigSchema, invalidMessage: "Die Blitzlesen-Konfiguration ist nicht gültig." },
  silbentrennung: { schema: silbentrennungExerciseConfigSchema, invalidMessage: "Die Konfiguration für Lesesilbentrennung ist nicht gültig." },
  reime: { schema: reimeExerciseConfigSchema, invalidMessage: "Die Reime-Konfiguration ist nicht gültig." },
  zeitformen: { schema: zeitformenExerciseConfigSchema, invalidMessage: "Die Zeitformen-Konfiguration ist nicht gültig." },
  "zeitformen-konjugieren": { schema: zeitformenKonjugierenExerciseConfigSchema, invalidMessage: "Die Konjugations-Konfiguration ist nicht gültig." },
  "dehnungs-h": { schema: dehnungsHExerciseConfigSchema, invalidMessage: "Die Dehnungs-h-Konfiguration ist nicht gültig." },
  "kurze-lange-vokale": { schema: kurzeLangeVokaleExerciseConfigSchema, invalidMessage: "Die Konfiguration für kurze und lange Vokale ist nicht gültig." },
  "morphem-stamm": { schema: morphemStammExerciseConfigSchema, invalidMessage: "Die Konfiguration für den Stamm-Trainer ist nicht gültig." },
  "morphem-zerlegung": { schema: morphemZerlegungExerciseConfigSchema, invalidMessage: "Die Konfiguration für die Morphem-Zerlegung ist nicht gültig." },
  wortarten: { schema: wortartenExerciseConfigSchema, invalidMessage: "Die Wortarten-Konfiguration ist nicht gültig." },
  grossklein: { schema: grosskleinExerciseConfigSchema, invalidMessage: "Die Groß-/Kleinschreib-Konfiguration ist nicht gültig." },
  satzglieder: { schema: satzgliederExerciseConfigSchema, invalidMessage: "Die Satzglieder-Konfiguration ist nicht gültig." },
} satisfies Record<ExerciseType, { schema: z.ZodType; invalidMessage: string }>;

export type ExerciseConfig = {
  [Type in ExerciseType]: z.infer<(typeof exerciseConfigRegistry)[Type]["schema"]>;
}[ExerciseType];

export const exerciseConfigSchema = z.union(Object.values(exerciseConfigRegistry).map(({ schema }) => schema) as unknown as [z.ZodType, z.ZodType, ...z.ZodType[]]);

const versionInputSchema = z.object({
  title: z.string().min(2).max(240),
  summary: z.string().max(1_000).default(""),
  blocks: contentDocumentSchema,
  exerciseType: exerciseTypeSchema.nullable().optional(),
  // Die konkrete Konfiguration wird erst zusammen mit exerciseType geprüft.
  // Eine nicht-diskriminierte Zod-Union würde bei ähnlichen Schemata (Dehnungs-h
  // und kurze/lange Vokale) zusätzliche Vokal-Hilfen vor der Fachprüfung entfernen.
  exerciseConfig: z.unknown().nullable().optional(),
});

export const draftInputSchema = versionInputSchema.superRefine((version, context) => {
  if (Boolean(version.exerciseType) !== Boolean(version.exerciseConfig)) {
    context.addIssue({
      code: "custom",
      path: ["exerciseConfig"],
      message: "Aufgabentyp und Konfiguration müssen gemeinsam gesetzt werden.",
    });
  }
  if (version.exerciseType) {
    const contract = exerciseConfigRegistry[version.exerciseType];
    if (!contract.schema.safeParse(version.exerciseConfig).success) {
      context.addIssue({ code: "custom", path: ["exerciseConfig"], message: contract.invalidMessage });
    }
  }
});

export const contentPageInputSchema = versionInputSchema
  .extend({
    slug: z
      .string()
      .min(2)
      .max(160)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Der Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten."),
    type: z.enum(["LEARNING", "EXERCISE"]),
  })
  .superRefine((page, context) => {
    if (page.type === "EXERCISE" && (!page.exerciseType || !page.exerciseConfig)) {
      context.addIssue({
        code: "custom",
        path: ["exerciseConfig"],
        message: "Eine Übungsseite benötigt einen Aufgabentyp und eine gültige Konfiguration.",
      });
    }
    if (page.type === "LEARNING" && (page.exerciseType || page.exerciseConfig)) {
      context.addIssue({
        code: "custom",
        path: ["exerciseConfig"],
        message: "Eine Lernseite darf keine Übungskonfiguration enthalten.",
      });
    }
    if (page.exerciseType === "stellenwert-addition" && !stellenwertAdditionExerciseConfigSchema.safeParse(page.exerciseConfig).success) {
      context.addIssue({ code: "custom", path: ["exerciseConfig"], message: "Die Konfiguration für ‚Zehner zuerst addieren‘ ist nicht gültig." });
    }
  });

const contentSyncSourceSchema = z.object({
  pageId: z.string().uuid(),
  publishedVersion: z.number().int().positive().nullable(),
  draftVersion: z.number().int().positive().nullable(),
});

export const contentSyncPageSchema = z.object({
  schemaVersion: z.literal(1),
  exportedAt: z.string().min(1),
  source: contentSyncSourceSchema,
  slug: z.string().min(2).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  type: z.enum(["LEARNING", "EXERCISE"]),
  title: z.string(),
  summary: z.string(),
  blocks: z.unknown(),
  exerciseType: z.string().max(120).nullable(),
  exerciseConfig: z.unknown().nullable(),
});

export const contentSyncImportPageSchema = contentSyncPageSchema.superRefine((page, context) => {
  const draft = draftInputSchema.safeParse({
    title: page.title,
    summary: page.summary,
    blocks: page.blocks,
    exerciseType: page.exerciseType,
    exerciseConfig: page.exerciseConfig,
  });
  if (!draft.success) {
    for (const issue of draft.error.issues) {
      context.addIssue({
        code: "custom",
        path: issue.path,
        message: issue.message,
      });
    }
  }
  if (page.type === "EXERCISE" && (!page.exerciseType || !page.exerciseConfig)) {
    context.addIssue({ code: "custom", path: ["exerciseConfig"], message: "Eine Übungsseite benötigt einen Aufgabentyp und eine gültige Konfiguration." });
  }
  if (page.type === "LEARNING" && (page.exerciseType || page.exerciseConfig)) {
    context.addIssue({ code: "custom", path: ["exerciseConfig"], message: "Eine Lernseite darf keine Übungskonfiguration enthalten." });
  }
});

export const contentSyncNavigationNodeSchema = z.object({
  id: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  type: z.enum(["SUBJECT", "TOPIC", "PAGE"]),
  title: z.string(),
  icon: z.string().nullable(),
  description: z.string(),
  position: z.number().int(),
  visible: z.boolean(),
  pageId: z.string().uuid().nullable(),
  pageSlug: z.string().nullable(),
  updatedAt: z.string().min(1),
});

export const contentSyncBundleSchema = z.object({
  schemaVersion: z.literal(1),
  exportedAt: z.string().min(1),
  pages: z.array(contentSyncPageSchema),
  navigation: z.array(contentSyncNavigationNodeSchema),
});

export type ContentBlock = z.infer<typeof contentBlockSchema>;
export type ContentDocument = z.infer<typeof contentDocumentSchema>;
export type LegalPageSlug = z.infer<typeof legalPageSlugSchema>;
export type LegalPageInput = z.infer<typeof legalPageInputSchema>;
export type QuizExerciseConfig = z.infer<typeof quizExerciseConfigSchema>;
export type LueckentextExerciseConfig = z.infer<typeof lueckentextExerciseConfigSchema>;
export type RechenmauerExerciseConfig = z.infer<typeof rechenmauerExerciseConfigSchema>;
export type RechenmauerRunSettings = z.infer<typeof rechenmauerRunSettingsSchema>;
export type MatheExerciseConfig = z.infer<typeof matheExerciseConfigSchema>;
export type MatheRunSettings = z.infer<typeof matheRunSettingsSchema>;
export type EinmaleinsReihe = z.infer<typeof einmaleinsReiheSchema>;
export type EinmaleinsExerciseConfig = z.infer<typeof einmaleinsExerciseConfigSchema>;
export type EinmaleinsRunSettings = z.infer<typeof einmaleinsRunSettingsSchema>;
export type StellenwertAdditionExerciseConfig = z.infer<typeof stellenwertAdditionExerciseConfigSchema>;
export type StellenwertAdditionRunSettings = z.infer<typeof stellenwertAdditionRunSettingsSchema>;
export type StellenwertAdditionPhase = z.infer<typeof stellenwertAdditionPhaseSchema>;
export type RundenExerciseConfig = z.infer<typeof rundenExerciseConfigSchema>;
export type RundenRunSettings = z.infer<typeof rundenRunSettingsSchema>;
export type RundenZahlenraum = z.infer<typeof rundenZahlenraumSchema>;
export type RundenStelle = z.infer<typeof rundenStelleSchema>;
export type ZahlenZerlegenExerciseConfig = z.infer<typeof zahlenZerlegenExerciseConfigSchema>;
export type ZahlenZerlegenRunSettings = z.infer<typeof zahlenZerlegenRunSettingsSchema>;
export type FroschHupfenExerciseConfig = z.infer<typeof froschHupfenExerciseConfigSchema>;
export type FroschHupfenRunSettings = z.infer<typeof froschHupfenRunSettingsSchema>;
export type WaageExerciseConfig = z.infer<typeof waageExerciseConfigSchema>;
export type WaageRunSettings = z.infer<typeof waageRunSettingsSchema>;
export type MengenMatchExerciseConfig = z.infer<typeof mengenMatchExerciseConfigSchema>;
export type MengenMatchRunSettings = z.infer<typeof mengenMatchRunSettingsSchema>;
export type ZaehlenTippenExerciseConfig = z.infer<typeof zaehlenTippenExerciseConfigSchema>;
export type ZaehlenTippenRunSettings = z.infer<typeof zaehlenTippenRunSettingsSchema>;
export type SortierspielExerciseConfig = z.infer<typeof sortierspielExerciseConfigSchema>;
export type SortierspielRunSettings = z.infer<typeof sortierspielRunSettingsSchema>;
export type WaageZahlenraum = z.infer<typeof waageZahlenraumSchema>;
export type WaageSchwierigkeit = z.infer<typeof waageSchwierigkeitSchema>;
export type WaageAnzahl = z.infer<typeof waageAnzahlSchema>;
export type SchriftlichRechnenExerciseConfig = z.infer<typeof schriftlichRechnenExerciseConfigSchema>;
export type SchriftlichRechnenRunSettings = z.infer<typeof schriftlichRechnenRunSettingsSchema>;
export type SchriftlichRechnenOperation = z.infer<typeof schriftlichRechnenOperationSchema>;
export type SchriftlichRechnenMultiplicationMethod = z.infer<typeof schriftlichRechnenMultiplicationMethodSchema>;
export type SchriftlichRechnenMultiplicationProfile = z.infer<typeof schriftlichRechnenMultiplicationProfileSchema>;
export type SchriftlichRechnenDivisionProfile = z.infer<typeof schriftlichRechnenDivisionProfileSchema>;
export type BlitzlesenExerciseConfig = z.infer<typeof blitzlesenExerciseConfigSchema>;
export type BlitzlesenRunSettings = z.infer<typeof blitzlesenRunSettingsSchema>;
export type IndividualExerciseCreateInput = z.infer<typeof individualExerciseCreateInputSchema>;
export type IndividualExerciseUpdateInput = z.infer<typeof individualExerciseUpdateInputSchema>;
export type SilbentrennungExerciseConfig = z.infer<typeof silbentrennungExerciseConfigSchema>;
export type SilbentrennungRunSettings = z.infer<typeof silbentrennungRunSettingsSchema>;
export type ReimeExerciseConfig = z.infer<typeof reimeExerciseConfigSchema>;
export type ReimeRunSettings = z.infer<typeof reimeRunSettingsSchema>;
export type ZeitformenExerciseConfig = z.infer<typeof zeitformenExerciseConfigSchema>;
export type ZeitformenRunSettings = z.infer<typeof zeitformenRunSettingsSchema>;
export type ZeitformenKonjugierenExerciseConfig = z.infer<typeof zeitformenKonjugierenExerciseConfigSchema>;
export type ZeitformenKonjugierenRunSettings = z.infer<typeof zeitformenKonjugierenRunSettingsSchema>;
export type SpellingChoiceItem = z.infer<typeof spellingChoiceItemSchema>;
export type DehnungsHExerciseConfig = z.infer<typeof dehnungsHExerciseConfigSchema>;
export type KurzeLangeVokaleExerciseConfig = z.infer<typeof kurzeLangeVokaleExerciseConfigSchema>;
export type DehnungsHRunSettings = z.infer<typeof dehnungsHRunSettingsSchema>;
export type KurzeLangeVokaleRunSettings = z.infer<typeof kurzeLangeVokaleRunSettingsSchema>;
export type MorphemStammItem = z.infer<typeof morphemStammItemSchema>;
export type MorphemStammExerciseConfig = z.infer<typeof morphemStammExerciseConfigSchema>;
export type MorphemStammRunSettings = z.infer<typeof morphemStammRunSettingsSchema>;
export type MorphemZerlegungItem = z.infer<typeof morphemZerlegungItemSchema>;
export type MorphemZerlegungExerciseConfig = z.infer<typeof morphemZerlegungExerciseConfigSchema>;
export type MorphemZerlegungRunSettings = z.infer<typeof morphemZerlegungRunSettingsSchema>;
export type Wordart = z.infer<typeof wordartSchema>;
export type WordartenExerciseConfig = z.infer<typeof wortartenExerciseConfigSchema>;
export type WordartenRunSettings = z.infer<typeof wortartenRunSettingsSchema>;
export type GrosskleinExerciseConfig = z.infer<typeof grosskleinExerciseConfigSchema>;
export type GrosskleinRunSettings = z.infer<typeof grosskleinRunSettingsSchema>;
export type SatzgliederExerciseConfig = z.infer<typeof satzgliederExerciseConfigSchema>;
export type SatzgliederRunSettings = z.infer<typeof satzgliederRunSettingsSchema>;
export type ContentPageInput = z.infer<typeof contentPageInputSchema>;
export type DraftInput = z.infer<typeof draftInputSchema>;
export type ContentSyncPage = z.infer<typeof contentSyncPageSchema>;
export type ContentSyncImportPage = z.infer<typeof contentSyncImportPageSchema>;
export type ContentSyncBundle = z.infer<typeof contentSyncBundleSchema>;

export type { RecommendationInput } from "./learning-contracts";
export * from "./worksheets";
export { mailSettingsSchema, defaultMailSettings, type MailSettings } from "./mail-settings";
export * from "./learning-display";
export * from "./exercise-pause";
export * from "./reflection";
export * from "./reading-progression";
export * from "./reading-materials";
export * from "./word-store";
export * from "./word-store-v2";
export * from "./word-catalog";
export * from "./word-suggestions";
export { wordStoreSeed, wordStoreLegacySeed, WORD_STORE_EXTENSIONS, WORD_STORE_DERIVATIONS } from "./word-store-seed";
export * from "./word-composition";
